export type LoadingSchedule = {
  steps: { at: number; text: string }[];
  // After the steps run out, cycle neutral messages so the screen never looks frozen.
  waiting?: { from: number; every: number; messages: string[] };
  longWait?: { at: number; text: string };
};

const STEP_SECONDS = 2;

// Spaces a plain list of steps evenly, holding on the last one.
export const toLoadingSchedule = (steps: string[]): LoadingSchedule => ({
  steps: steps.map((text, index) => ({ at: index * STEP_SECONDS, text })),
});

const isWaiting = (schedule: LoadingSchedule, elapsedSeconds: number) =>
  Boolean(schedule.waiting) && elapsedSeconds >= schedule.waiting!.from;

const isLongWait = (schedule: LoadingSchedule, elapsedSeconds: number) =>
  Boolean(schedule.longWait) && elapsedSeconds >= schedule.longWait!.at;

export const getLoadingMessage = (
  schedule: LoadingSchedule,
  elapsedSeconds: number
): string => {
  if (isLongWait(schedule, elapsedSeconds)) {
    return schedule.longWait!.text;
  }

  if (isWaiting(schedule, elapsedSeconds)) {
    const { from, every, messages } = schedule.waiting!;
    return messages[Math.floor((elapsedSeconds - from) / every) % messages.length];
  }

  const current = [...schedule.steps]
    .reverse()
    .find((step) => elapsedSeconds >= step.at);
  return current?.text ?? schedule.steps[0]?.text ?? "";
};

// Screen readers hear the steps and the long-wait notice, not every rotating waiting message.
export const isLoadingMessageAnnounced = (
  schedule: LoadingSchedule,
  elapsedSeconds: number
) =>
  isLongWait(schedule, elapsedSeconds) || !isWaiting(schedule, elapsedSeconds);
